import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Content } from 'src/contexts/ContentContext';
import { SimpleTable } from 'src/core/ui';
import { useEventMarketplaceSettings } from 'src/hooks/useEventMarketplaceSettings';
import { MultiDetailSection } from 'src/modals/common';
import { BulkEditListingGroupSettingsFields } from 'src/modals/GroupListings/components/groupingTypes';
import { ContentId } from 'src/utils/constants/contentId';
import { compareMarketplace } from 'src/utils/eventWithDataUtils';
import { isMarketplaceActive } from 'src/utils/userUtils';
import { Event, Marketplace } from 'src/WebApiController';

import { MarketplaceDisplay } from './MarketplaceDisplay';
import * as styles from './MarketplaceDisplay.css';

/**
 * Get marketplace id from marketplace enum.
 * This assumes the enum order is aligned with the Marketplace.Marketplace table
 */
const getMarketplaceIdFromMarketplace = (marketplace: Marketplace) => {
  let marketplaceId = 0;
  Object.values(Marketplace).forEach((mp, index) => {
    if (mp === marketplace) {
      marketplaceId = index + 1;
    }
  });
  return marketplaceId;
};

type BulkEditListingGroupSettingMarketplaceBroadcastProps = {
  event?: Event;
};

export const BulkEditListingGroupSettingsMarketplaceBroadcast = ({
  event,
}: BulkEditListingGroupSettingMarketplaceBroadcastProps) => {
  const { watch, setValue } =
    useFormContext<BulkEditListingGroupSettingsFields>();

  const marketplaceSettings = watch('marketplaceSettings');

  const { eventMarketplaceSettings, isUKEvent, isInternationalEvent } =
    useEventMarketplaceSettings(event);

  const viagogoInternationalSetting = useMemo(
    () =>
      eventMarketplaceSettings.find(
        (mk) => mk.mkp === Marketplace.ViagogoInternational
      ),
    [eventMarketplaceSettings]
  );

  const onListingBroadcastToggle = useCallback(
    (isSelected: boolean, isBroadcasting: boolean, mp: Marketplace) => {
      const marketplaceId = getMarketplaceIdFromMarketplace(mp);
      const copied = { ...marketplaceSettings?.listingMarketplaceSettings };

      if (isSelected) {
        copied[marketplaceId] = {
          listingBroadcastStateId: isBroadcasting ? 2 : 1,
        };
      } else {
        delete copied[marketplaceId];
      }

      // §3 coupling: ViagogoInternational ↔ StubHub
      if (viagogoInternationalSetting) {
        const stubHubId = getMarketplaceIdFromMarketplace(Marketplace.StubHub);
        const viId = getMarketplaceIdFromMarketplace(
          Marketplace.ViagogoInternational
        );

        if (
          mp === Marketplace.ViagogoInternational &&
          isBroadcasting &&
          copied[stubHubId]?.listingBroadcastStateId !== 2
        ) {
          // Broadcasting ViagogoInternational → also broadcast StubHub
          copied[stubHubId] = { listingBroadcastStateId: 2 };
        } else if (
          mp === Marketplace.StubHub &&
          !isBroadcasting &&
          copied[viId]?.listingBroadcastStateId === 2
        ) {
          // Unbroadcasting StubHub → also unbroadcast ViagogoInternational
          copied[viId] = { listingBroadcastStateId: 1 };
        }
      }

      setValue('marketplaceSettings', { listingMarketplaceSettings: copied });
    },
    [marketplaceSettings, setValue, viagogoInternationalSetting]
  );

  const marketplacesSelected = useMemo(() => {
    const selected: Marketplace[] = [];
    const settings = marketplaceSettings?.listingMarketplaceSettings ?? [];
    const includedIds = Object.keys(settings);
    Object.values(Marketplace).forEach((mp, index) => {
      if (includedIds.includes(index + 1 + '')) {
        selected.push(mp);
      }
    });
    return selected;
  }, [marketplaceSettings]);

  const marketplaceToStateId = useMemo(() => {
    const marketplaceToId = {} as Record<Marketplace, number>;
    const settings = marketplaceSettings?.listingMarketplaceSettings;
    const includedIds = settings != null ? Object.keys(settings) : [];
    Object.values(Marketplace).forEach((mp, index) => {
      const key = index + 1 + '';
      if (includedIds.includes(key) && settings != null) {
        marketplaceToId[mp] = settings[key].listingBroadcastStateId;
      }
    });
    return marketplaceToId;
  }, [marketplaceSettings]);

  return (
    <MultiDetailSection
      name={<Content id={ContentId.Broadcast} />}
      extraContent={[]}
    >
      <SimpleTable.Table>
        <SimpleTable.Thead>
          <SimpleTable.Tr>
            <SimpleTable.Th
              className={`${styles.broadcastHeaderCell} ${styles.broadcastCell}`}
            ></SimpleTable.Th>
            <SimpleTable.Th
              className={`${styles.broadcastHeaderCell} ${styles.broadcastCell}`}
            >
              <Content id={ContentId.Merchant} />
            </SimpleTable.Th>
            <SimpleTable.Th
              className={`${styles.broadcastHeaderCell} ${styles.broadcastCell}`}
            >
              <Content id={ContentId.Broadcast} />
            </SimpleTable.Th>
            <SimpleTable.Th
              className={`${styles.broadcastHeaderCell} ${styles.broadcastCell}`}
            >
              <Content id={ContentId.Status} />
            </SimpleTable.Th>
          </SimpleTable.Tr>
        </SimpleTable.Thead>
        <SimpleTable.Tbody>
          {eventMarketplaceSettings
            .toSorted((a, b) => compareMarketplace(a.mkp, b.mkp))
            .flatMap((mk) => {
              // ViagogoInternational is rendered as companion to StubHub below
              if (mk.mkp === Marketplace.ViagogoInternational) {
                return [];
              }

              const isSelected = marketplacesSelected.includes(mk.mkp);
              const isActive = marketplaceToStateId[mk.mkp] === 2;

              if (mk.mkp === Marketplace.StubHub) {
                const viMkp = viagogoInternationalSetting?.mkp;
                const viIsSelected = viMkp
                  ? marketplacesSelected.includes(viMkp)
                  : isSelected;
                const viIsActive = viMkp
                  ? marketplaceToStateId[viMkp] === 2
                  : isActive;

                return [
                  <MarketplaceDisplay
                    key={`${mk}-${false}`}
                    marketplace={mk.mkp}
                    marketplaceActive={isMarketplaceActive(mk)}
                    isActive={isActive}
                    isSelected={isSelected}
                    onListingBroadcastToggle={onListingBroadcastToggle}
                    isLoading={false}
                    isUKEvent={isUKEvent}
                  />,
                  <MarketplaceDisplay
                    key={`${mk}-${true}`}
                    marketplace={viMkp ?? mk.mkp}
                    marketplaceActive={isMarketplaceActive(
                      viagogoInternationalSetting ?? mk
                    )}
                    isViagogo
                    isActive={viIsActive}
                    isSelected={viIsSelected}
                    onListingBroadcastToggle={onListingBroadcastToggle}
                    isLoading={false}
                  />,
                ];
              }

              if (!event || !isInternationalEvent) {
                return [
                  <MarketplaceDisplay
                    key={`${mk}-${false}`}
                    marketplace={mk.mkp}
                    marketplaceActive={isMarketplaceActive(mk)}
                    isActive={isActive}
                    isSelected={isSelected}
                    onListingBroadcastToggle={onListingBroadcastToggle}
                    isLoading={false}
                  />,
                ];
              }

              // Only support Stubhub/Viagogo for international events
              return [];
            })}
        </SimpleTable.Tbody>
      </SimpleTable.Table>
    </MultiDetailSection>
  );
};
